import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'

import Page from '../../index'
import Button from '../../../components/Button'
import AppLoader from '../../../components/Loaders/AppLoader'
import InfoBlock from '../../../features/components/InfoBlock'

import usePlatformConnected from '../../../features/hooks/usePlatformConnected'

import { showToasts } from '../../../helpers/toasts'
import { createJsonFromQueryString, redirectTo } from '../../../helpers/url'
import { getSelectedPlatform } from '../../../helpers/handlePlatformSelection'
import { getLocalStorage, setLocalStorage } from '../../../helpers/storage'

import { socialAuth } from '../../../modules/actions/auth'
import { setUserAccountSetupMode } from '../../../modules/actions/app'
import { isAccountSetupModeSelector } from '../../../modules/selectors/app'
import { isSocialAuthLoadingSelector, socialAuthErrorSelector } from '../../../modules/selectors/auth'

import { TOAST_TYPE } from '../../../constants/other'

import useCommonStyles from '../style'

function SocialAuthorizeCallback() {
  const commonClasses = useCommonStyles()
  const dispatch = useDispatch()

  const socialAuthError = useSelector(socialAuthErrorSelector)
  const isAccountSetupMode = useSelector(isAccountSetupModeSelector)
  const isSocialAuthLoading = useSelector(isSocialAuthLoadingSelector)
  const selectedPlatform = getSelectedPlatform()

  const isPlatformConnected = usePlatformConnected(selectedPlatform, true)

  const { search } = useLocation()
  const { state, code, scope } = createJsonFromQueryString(search)

  const authParams = useMemo(() => {
    return { state, code, scope }
  }, [state, code, scope])

  const handleRedirectToMainApp = useCallback(() => {
    // SocialAuthorizeCallback is used only for AccountSetupMode to avoid trigerring partnerships fetching and platform preselect
    // so after it was processed we need to turn of AccountSetupMode and redirect to Main app
    dispatch(setUserAccountSetupMode(false))

    // redirect for landing URL after login
    const authTriggeringUrl = getLocalStorage('authTriggeringUrl')

    redirectTo(authTriggeringUrl || '/')

    setLocalStorage('authTriggeringUrl', '')
  }, [dispatch])

  useEffect(() => {
    // Make ajax GET request to /api/allauth/google/login/callback/
    if (!isPlatformConnected) {
      // send request only if platform is not connected to avoid unnecessary requests
      dispatch(socialAuth({ authParams, selectedPlatform }))
    }
  }, [authParams, selectedPlatform, dispatch, isPlatformConnected])

  // Once ajax GET request is done then redirect back to authorise-success page
  useEffect(() => {
    if (isAccountSetupMode && !isSocialAuthLoading && isPlatformConnected) {
      showToasts({
        type: TOAST_TYPE.success,
        message: 'Platform was connected successfully.'
      })

      handleRedirectToMainApp()
    }
  }, [handleRedirectToMainApp, isSocialAuthLoading, isPlatformConnected, isAccountSetupMode])

  return (
    <Page helmetTitle="Social Authorize Callback" className={commonClasses.socialAuthPage}>
      {socialAuthError ? (
        <InfoBlock title={socialAuthError.message} centered greyDescription>
          <Button onClick={handleRedirectToMainApp}>Return to partnerships</Button>
        </InfoBlock>
      ) : (
        <AppLoader showSideMenu />
      )}
    </Page>
  )
}

export default SocialAuthorizeCallback
